import React, { useState, useEffect } from 'react';
import { 
    Card, 
    Row, 
    Col, 
    Statistic, 
    Select, 
    DatePicker, 
    Typography, 
    Table, 
    Progress, 
    Tag, 
    Space,
    Button,
    Tooltip,
    Alert,
    Tabs,
    List,
    Avatar,
    Divider,
    Empty,
    Spin,
    notification
} from 'antd';
import { 
    UserOutlined, 
    ShopOutlined, 
    ShoppingCartOutlined, 
    DollarOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    EyeOutlined,
    StarOutlined,
    BarChartOutlined,
    PieChartOutlined,
    LineChartOutlined,
    DownloadOutlined,
    ReloadOutlined,
    FilterOutlined,
    CalendarOutlined,
    TeamOutlined,
    CrownOutlined,
    FireOutlined,
    ThunderboltOutlined,
    RiseOutlined,
    FallOutlined,
    ClockCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../../services/api';
import { formatCurrency, getStatusColor } from '../../../utils/ui';

const { RangePicker } = DatePicker;
const { Title, Paragraph } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

function AdminAnalytics() {
    const [dateRange, setDateRange] = useState([
        dayjs().subtract(30, 'days'),
        dayjs()
    ]);
    const [selectedMetric, setSelectedMetric] = useState('revenue');
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('overview');
    const [refreshing, setRefreshing] = useState(false);

    // Enhanced analytics data with comprehensive metrics
    const [analytics, setAnalytics] = useState({
        overview: {
            totalUsers: 1247,
            totalVendors: 89,
            totalBookings: 456,
            totalRevenue: 2345678,
            userGrowth: 12.5,
            revenueGrowth: 8.3,
            conversionRate: 3.2,
            avgOrderValue: 5147,
            activeUsers: 892,
            pendingVerifications: 12,
            monthlyRecurring: 234,
            churnRate: 2.1
        },
        revenueBreakdown: {
            commission: 234567,
            subscriptions: 45678,
            advertisements: 12345,
            other: 8901
        },
        topVendors: [
            { id: 1, name: 'Royal Caterers', bookings: 45, revenue: 234000, rating: 4.8, growth: 15.2, city: 'Mumbai' },
            { id: 2, name: 'Delicious Delights', bookings: 38, revenue: 198000, rating: 4.6, growth: 12.8, city: 'Bengaluru' },
            { id: 3, name: 'Gourmet Kitchen', bookings: 32, revenue: 167000, rating: 4.7, growth: 8.5, city: 'Delhi' },
            { id: 4, name: 'Spice Garden', bookings: 28, revenue: 145000, rating: 4.5, growth: 22.1, city: 'Pune' },
            { id: 5, name: 'Taste of India', bookings: 25, revenue: 132000, rating: 4.4, growth: 5.3, city: 'Chennai' }
        ],
        topCities: [
            { name: 'Mumbai', bookings: 156, revenue: 789000, growth: 15.2, vendors: 23, avgRating: 4.6 },
            { name: 'Bengaluru', bookings: 134, revenue: 678000, growth: 12.8, vendors: 19, avgRating: 4.5 },
            { name: 'Delhi NCR', bookings: 98, revenue: 456000, growth: 8.5, vendors: 15, avgRating: 4.4 },
            { name: 'Pune', bookings: 45, revenue: 234000, growth: 22.1, vendors: 12, avgRating: 4.7 },
            { name: 'Chennai', bookings: 23, revenue: 134000, growth: 5.3, vendors: 8, avgRating: 4.3 }
        ],
        recentActivity: [
            { id: 1, type: 'booking', message: 'New booking from Royal Caterers', time: '5 mins ago', status: 'success' },
            { id: 2, type: 'vendor', message: 'Spice Garden completed verification', time: '15 mins ago', status: 'info' },
            { id: 3, type: 'payment', message: 'Payment of ₹45,000 processed', time: '1 hour ago', status: 'success' },
            { id: 4, type: 'issue', message: 'Customer complaint resolved', time: '2 hours ago', status: 'warning' },
            { id: 5, type: 'user', message: '12 new user registrations', time: '3 hours ago', status: 'info' }
        ],
        performanceMetrics: {
            pageViews: 45678,
            uniqueVisitors: 12345,
            bounceRate: 23.4,
            avgSessionDuration: '4m 32s',
            mobileTraffic: 67.8,
            conversionFunnel: {
                visitors: 12345,
                signups: 892,
                bookings: 456,
                completed: 423
            }
        }
    });

    useEffect(() => {
        fetchAnalytics();
    }, [dateRange, selectedMetric]);

    const fetchAnalytics = async () => {
        setLoading(true);
        try {
            const [usersRes, vendorsRes, bookingsRes, activityRes] = await Promise.all([
                api.adminListUsers({ per_page: 1 }),
                api.adminListVendors({ per_page: 1 }),
                api.adminListBookings({ per_page: 1 }),
                api.adminGetActivityLogs({ per_page: 10 })
            ]);

            const totalUsers = usersRes?.data?.total ?? usersRes?.data?.meta?.total ?? usersRes?.data?.data?.length ?? 0;
            const totalVendors = vendorsRes?.data?.total ?? vendorsRes?.data?.meta?.total ?? vendorsRes?.data?.data?.length ?? 0;
            const totalBookings = bookingsRes?.data?.total ?? bookingsRes?.data?.meta?.total ?? bookingsRes?.data?.data?.length ?? 0;

            setAnalytics((prev) => ({
                ...prev,
                overview: {
                    ...prev.overview,
                    totalUsers,
                    totalVendors,
                    totalBookings,
                },
                recentActivity: activityRes?.data?.data || prev.recentActivity
            }));

            notification.success({
                message: 'Analytics Updated',
                description: 'Dashboard data has been refreshed successfully.',
                duration: 2
            });
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to fetch analytics data.'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        await fetchAnalytics();
        setRefreshing(false);
    };

    const exportData = (type) => {
        notification.info({
            message: 'Export Started',
            description: `${type} export will be ready for download shortly.`
        });
    };

    const vendorColumns = [
        {
            title: 'Vendor',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <div className="flex items-center gap-2">
                    <Avatar icon={<ShopOutlined />} size="small" />
                    <div>
                        <div className="font-medium">{text}</div>
                        <div className="text-xs text-gray-500">{record.city}</div>
                    </div>
                </div>
            )
        },
        {
            title: 'Bookings',
            dataIndex: 'bookings',
            key: 'bookings',
            render: (value) => (
                <div className="text-center">
                    <div className="font-semibold">{value}</div>
                </div>
            )
        },
        {
            title: 'Revenue',
            dataIndex: 'revenue',
            key: 'revenue',
            render: (value) => (
                <div className="text-green-600 font-medium">
                    {formatCurrency(value)}
                </div>
            )
        },
        {
            title: 'Rating',
            dataIndex: 'rating',
            key: 'rating',
            render: (value) => (
                <div className="flex items-center gap-1">
                    <StarOutlined className="text-yellow-500" />
                    <span className="font-medium">{value}</span>
                </div>
            )
        },
        {
            title: 'Growth',
            dataIndex: 'growth',
            key: 'growth',
            render: (value) => (
                <Tag color={getStatusColor(value > 0 ? 'success' : 'cancelled')}>
                    {value > 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                    {Math.abs(value)}%
                </Tag>
            )
        }
    ];

    const cityColumns = [
        {
            title: 'City',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <div>
                    <div className="font-medium">{text}</div>
                    <div className="text-xs text-gray-500">{record.vendors} vendors</div>
                </div>
            )
        },
        {
            title: 'Performance',
            key: 'performance',
            render: (_, record) => (
                <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                        <span>Bookings: {record.bookings}</span>
                        <span className="text-green-600">{formatCurrency(record.revenue)}</span>
                    </div>
                    <Progress 
                        percent={Math.min((record.bookings / 200) * 100, 100)} 
                        size="small" 
                        showInfo={false}
                    />
                </div>
            )
        },
        {
            title: 'Avg Rating',
            dataIndex: 'avgRating',
            key: 'avgRating',
            render: (value) => (
                <div className="flex items-center gap-1">
                    <StarOutlined className="text-yellow-500" />
                    <span>{value}</span>
                </div>
            )
        },
        {
            title: 'Growth',
            dataIndex: 'growth',
            key: 'growth',
            render: (value) => (
                <Tag color={value > 15 ? 'green' : value > 5 ? 'blue' : 'orange'}>
                    {value > 0 ? <TrendingUpOutlined /> : <TrendingDownOutlined />}
                    {Math.abs(value)}%
                </Tag>
            )
        }
    ];

    const renderOverviewCards = () => (
        <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={6}>
                <Card className="hover:shadow-lg transition-shadow border-l-4 border-l-blue-500">
                    <Statistic
                        title="Total Users"
                        value={analytics.overview.totalUsers}
                        prefix={<UserOutlined className="text-blue-500" />}
                        suffix={
                            <Tag color="green" size="small">
                                <ArrowUpOutlined /> {analytics.overview.userGrowth}%
                            </Tag>
                        }
                    />
                    <div className="mt-2 text-xs text-gray-500">
                        Active: {analytics.overview.activeUsers}
                    </div>
                </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
                <Card className="hover:shadow-lg transition-shadow border-l-4 border-l-green-500">
                    <Statistic
                        title="Total Vendors"
                        value={analytics.overview.totalVendors}
                        prefix={<ShopOutlined className="text-green-500" />}
                        suffix={
                            analytics.overview.pendingVerifications > 0 && (
                                <Tag color="orange" size="small">
                                    {analytics.overview.pendingVerifications} pending
                                </Tag>
                            )
                        }
                    />
                    <div className="mt-2 text-xs text-gray-500">
                        Verified & Active
                    </div>
                </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
                <Card className="hover:shadow-lg transition-shadow border-l-4 border-l-purple-500">
                    <Statistic
                        title="Total Bookings"
                        value={analytics.overview.totalBookings}
                        prefix={<ShoppingCartOutlined className="text-purple-500" />}
                        suffix={
                            <Tag color="blue" size="small">
                                {analytics.overview.conversionRate}% CVR
                            </Tag>
                        }
                    />
                    <div className="mt-2 text-xs text-gray-500">
                        Recurring: {analytics.overview.monthlyRecurring}
                    </div>
                </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
                <Card className="hover:shadow-lg transition-shadow border-l-4 border-l-orange-500">
                    <Statistic
                        title="Total Revenue"
                        value={formatCurrency(analytics.overview.totalRevenue)}
                        prefix={<DollarOutlined className="text-orange-500" />}
                        suffix={
                            <Tag color="green" size="small">
                                <ArrowUpOutlined /> {analytics.overview.revenueGrowth}%
                            </Tag>
                        }
                    />
                    <div className="mt-2 text-xs text-gray-500">
                        AOV: {formatCurrency(analytics.overview.avgOrderValue)}
                    </div>
                </Card>
            </Col>
        </Row>
    );

    const renderRevenueBreakdown = () => (
        <Card title="Revenue Breakdown" className="h-full">
            <Row gutter={[16, 16]}>
                <Col span={12}>
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600">
                            {formatCurrency(analytics.revenueBreakdown.commission)}
                        </div>
                        <div className="text-sm text-gray-600">Commission</div>
                        <div className="text-xs text-green-600">+12.5%</div>
                    </div>
                </Col>
                <Col span={12}>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                        <div className="text-2xl font-bold text-green-600">
                            {formatCurrency(analytics.revenueBreakdown.subscriptions)}
                        </div>
                        <div className="text-sm text-gray-600">Subscriptions</div>
                        <div className="text-xs text-green-600">+8.3%</div>
                    </div>
                </Col>
                <Col span={12}>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                        <div className="text-2xl font-bold text-purple-600">
                            {formatCurrency(analytics.revenueBreakdown.advertisements)}
                        </div>
                        <div className="text-sm text-gray-600">Ads</div>
                        <div className="text-xs text-green-600">+15.7%</div>
                    </div>
                </Col>
                <Col span={12}>
                    <div className="text-center p-4 bg-orange-50 rounded-lg">
                        <div className="text-2xl font-bold text-orange-600">
                            {formatCurrency(analytics.revenueBreakdown.other)}
                        </div>
                        <div className="text-sm text-gray-600">Other</div>
                        <div className="text-xs text-red-600">-2.1%</div>
                    </div>
                </Col>
            </Row>
        </Card>
    );

    const renderPerformanceMetrics = () => (
        <Card title="Performance Metrics" className="h-full">
            <Row gutter={[16, 16]}>
                <Col span={12}>
                    <Statistic
                        title="Page Views"
                        value={analytics.performanceMetrics.pageViews}
                        prefix={<EyeOutlined />}
                    />
                </Col>
                <Col span={12}>
                    <Statistic
                        title="Unique Visitors"
                        value={analytics.performanceMetrics.uniqueVisitors}
                        prefix={<TeamOutlined />}
                    />
                </Col>
                <Col span={12}>
                    <Statistic
                        title="Bounce Rate"
                        value={analytics.performanceMetrics.bounceRate}
                        suffix="%"
                        valueStyle={{ color: analytics.performanceMetrics.bounceRate > 30 ? '#cf1322' : '#3f8600' }}
                    />
                </Col>
                <Col span={12}>
                    <Statistic
                        title="Session Duration"
                        value={analytics.performanceMetrics.avgSessionDuration}
                        prefix={<ClockCircleOutlined />}
                    />
                </Col>
            </Row>
            <Divider />
            <div>
                <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium">Mobile Traffic</span>
                    <span className="text-sm">{analytics.performanceMetrics.mobileTraffic}%</span>
                </div>
                <Progress percent={analytics.performanceMetrics.mobileTraffic} />
            </div>
        </Card>
    );

    const renderConversionFunnel = () => (
        <Card title="Conversion Funnel" className="h-full">
            <div className="space-y-4">
                {Object.entries(analytics.performanceMetrics.conversionFunnel).map(([key, value], index) => {
                    const percentage = index === 0 ? 100 : (value / analytics.performanceMetrics.conversionFunnel.visitors) * 100;
                    return (
                        <div key={key} className="flex items-center justify-between">
                            <div className="flex-1">
                                <div className="flex justify-between items-center mb-1">
                                    <span className="text-sm font-medium capitalize">{key}</span>
                                    <span className="text-sm text-gray-500">{value.toLocaleString()}</span>
                                </div>
                                <Progress 
                                    percent={percentage} 
                                    size="small"
                                    strokeColor={index === 0 ? '#1890ff' : index === 1 ? '#52c41a' : index === 2 ? '#faad14' : '#f5222d'}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </Card>
    );

    const renderRecentActivity = () => (
        <Card 
            title="Recent Activity" 
            extra={
                <Button type="text" icon={<ReloadOutlined />} onClick={handleRefresh} loading={refreshing}>
                    Refresh
                </Button>
            }
            className="h-full"
        >
            <List
                dataSource={analytics.recentActivity}
                renderItem={(item) => (
                    <List.Item className="px-0">
                        <List.Item.Meta
                            avatar={
                                <Avatar 
                                    icon={
                                        item.type === 'booking' ? <ShoppingCartOutlined /> :
                                        item.type === 'vendor' ? <ShopOutlined /> :
                                        item.type === 'payment' ? <DollarOutlined /> :
                                        item.type === 'user' ? <UserOutlined /> :
                                        <FireOutlined />
                                    }
                                    style={{
                                        backgroundColor: 
                                            item.status === 'success' ? '#52c41a' :
                                            item.status === 'warning' ? '#faad14' :
                                            item.status === 'info' ? '#1890ff' : '#f5222d'
                                    }}
                                />
                            }
                            title={item.message}
                            description={item.time}
                        />
                    </List.Item>
                )}
            />
        </Card>
    );

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
                    <div>
                        <Title level={2} className="!mb-2">📊 Analytics Dashboard</Title>
                        <Paragraph className="text-gray-600 !mb-0">
                            Comprehensive insights into your catering platform performance
                        </Paragraph>
                    </div>
                    
                    <div className="flex flex-wrap gap-2 mt-4 lg:mt-0">
                        <RangePicker
                            value={dateRange}
                            onChange={setDateRange}
                            className="w-full sm:w-auto"
                        />
                        <Select
                            value={selectedMetric}
                            onChange={setSelectedMetric}
                            className="w-full sm:w-32"
                        >
                            <Option value="revenue">Revenue</Option>
                            <Option value="bookings">Bookings</Option>
                            <Option value="users">Users</Option>
                            <Option value="vendors">Vendors</Option>
                        </Select>
                        <Button 
                            type="primary" 
                            icon={<DownloadOutlined />}
                            onClick={() => exportData('analytics')}
                        >
                            Export
                        </Button>
                    </div>
                </div>

                {/* Alert for important metrics */}
                {analytics.overview.pendingVerifications > 10 && (
                    <Alert
                        message="High Pending Verifications"
                        description={`${analytics.overview.pendingVerifications} vendors are waiting for verification. Consider reviewing them soon.`}
                        type="warning"
                        showIcon
                        closable
                        className="mb-6"
                    />
                )}

                <Spin spinning={loading}>
                    <Tabs activeKey={activeTab} onChange={setActiveTab} size="large">
                        <TabPane tab={<span><BarChartOutlined /> Overview</span>} key="overview">
                            <div className="space-y-6">
                                {renderOverviewCards()}
                                
                                <Row gutter={[16, 16]}>
                                    <Col xs={24} lg={12}>
                                        {renderRevenueBreakdown()}
                                    </Col>
                                    <Col xs={24} lg={12}>
                                        {renderPerformanceMetrics()}
                                    </Col>
                                </Row>

                                <Row gutter={[16, 16]}>
                                    <Col xs={24} lg={12}>
                                        {renderConversionFunnel()}
                                    </Col>
                                    <Col xs={24} lg={12}>
                                        {renderRecentActivity()}
                                    </Col>
                                </Row>
                            </div>
                        </TabPane>

                        <TabPane tab={<span><ShopOutlined /> Vendors</span>} key="vendors">
                            <Card 
                                title="Top Performing Vendors" 
                                extra={
                                    <Button icon={<DownloadOutlined />} onClick={() => exportData('vendors')}>
                                        Export Vendors
                                    </Button>
                                }
                            >
                                <Table
                                    columns={vendorColumns}
                                    dataSource={analytics.topVendors}
                                    rowKey="id"
                                    pagination={false}
                                    className="vendor-analytics-table"
                                />
                            </Card>
                        </TabPane>

                        <TabPane tab={<span><TeamOutlined /> Cities</span>} key="cities">
                            <Card 
                                title="City Performance Analysis" 
                                extra={
                                    <Button icon={<DownloadOutlined />} onClick={() => exportData('cities')}>
                                        Export Cities
                                    </Button>
                                }
                            >
                                <Table
                                    columns={cityColumns}
                                    dataSource={analytics.topCities}
                                    rowKey="name"
                                    pagination={false}
                                />
                            </Card>
                        </TabPane>

                        <TabPane tab={<span><LineChartOutlined /> Performance</span>} key="performance">
                            <Row gutter={[16, 16]}>
                                <Col xs={24} lg={12}>
                                    {renderPerformanceMetrics()}
                                </Col>
                                <Col xs={24} lg={12}>
                                    {renderConversionFunnel()}
                                </Col>
                            </Row>
                        </TabPane>
                    </Tabs>
                </Spin>
            </div>
        </div>
    );
}

export default AdminAnalytics;
